// ============================================================================
// Hiring filter bar (Antonela spec §4.6) — shared by Board and List views.
//
// Dimensions:
//   * Mine / All toggle (only for AM / HR / superuser). "Mine" = I requested
//     it OR I'm the AM on its facility (ownership logic lives in index.tsx).
//   * Hospital — multi-select of Active accounts (searchable checkbox popover;
//     ~60 options is too many for chips).
//   * Priority — High / Medium / Low chips.
//   * Stage — the 7 stage chips.
// ============================================================================

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  HIRING_STAGES,
  HIRING_STAGE_LABELS,
  type HiringRequestStatus,
  type HiringUrgency,
} from '../../shared/types';
import { overlayClickClose } from '../../shared/overlay-close';

export interface HiringFilterState {
  scope: 'mine' | 'all';
  hospitals: string[]; // account_ids; empty = no filter
  priorities: HiringUrgency[]; // empty = no filter
  stages: HiringRequestStatus[]; // empty = no filter
}

export const DEFAULT_HIRING_FILTERS: HiringFilterState = {
  scope: 'all',
  hospitals: [],
  priorities: [],
  stages: [],
};

const PRIORITIES: HiringUrgency[] = ['High', 'Medium', 'Low'];

interface HospitalOption {
  account_id: string;
  account_name: string | null;
}

interface Props {
  filters: HiringFilterState;
  onChange: (next: HiringFilterState) => void;
  hospitalOptions: HospitalOption[];
  showScope: boolean;
  visibleCount: number;
  totalCount: number;
}

export default function HiringFilters({
  filters,
  onChange,
  hospitalOptions,
  showScope,
  visibleCount,
  totalCount,
}: Props) {
  const togglePriority = (p: HiringUrgency) =>
    onChange({
      ...filters,
      priorities: filters.priorities.includes(p)
        ? filters.priorities.filter((x) => x !== p)
        : [...filters.priorities, p],
    });

  const toggleStage = (s: HiringRequestStatus) =>
    onChange({
      ...filters,
      stages: filters.stages.includes(s)
        ? filters.stages.filter((x) => x !== s)
        : [...filters.stages, s],
    });

  const toggleHospital = (id: string) =>
    onChange({
      ...filters,
      hospitals: filters.hospitals.includes(id)
        ? filters.hospitals.filter((x) => x !== id)
        : [...filters.hospitals, id],
    });

  const isFiltered =
    filters.hospitals.length > 0 ||
    filters.priorities.length > 0 ||
    filters.stages.length > 0 ||
    (showScope && filters.scope !== DEFAULT_HIRING_FILTERS.scope);

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
      <div className="flex flex-wrap items-center gap-2">
        {showScope && (
          <Segmented
            value={filters.scope}
            onChange={(v) => onChange({ ...filters, scope: v })}
            options={[
              { value: 'mine', label: 'Mine' },
              { value: 'all', label: 'All' },
            ]}
            ariaLabel="Scope filter"
          />
        )}

        <HospitalPicker
          options={hospitalOptions}
          selected={filters.hospitals}
          onToggle={toggleHospital}
          onClear={() => onChange({ ...filters, hospitals: [] })}
        />

        {isFiltered && (
          <button
            type="button"
            onClick={() =>
              onChange({
                ...DEFAULT_HIRING_FILTERS,
                scope: showScope ? filters.scope : 'all',
              })
            }
            className="rounded-md border border-tcs-border px-2 py-1 text-xs text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Reset filters
          </button>
        )}
      </div>

      {/* Priority chips */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
          Priority
        </span>
        {PRIORITIES.map((p) => (
          <Chip
            key={p}
            active={filters.priorities.includes(p)}
            onClick={() => togglePriority(p)}
            label={p}
          />
        ))}
      </div>

      {/* Stage chips */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
          Stage
        </span>
        {HIRING_STAGES.map((s) => (
          <Chip
            key={s}
            active={filters.stages.includes(s)}
            onClick={() => toggleStage(s)}
            label={HIRING_STAGE_LABELS[s]}
          />
        ))}
      </div>

      <div className="mt-2 text-right text-[11px] text-tcs-text-muted">
        Showing <strong className="text-tcs-text-secondary">{visibleCount}</strong> of{' '}
        {totalCount}
      </div>
    </div>
  );
}

function HospitalPicker({
  options,
  selected,
  onToggle,
  onClear,
}: {
  options: HospitalOption[];
  selected: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const filtered = term
    ? options.filter((o) => (o.account_name ?? '').toLowerCase().includes(term))
    : options;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-2.5 py-1 text-xs text-tcs-text-secondary hover:bg-tcs-bg-muted"
      >
        Hospital
        {selected.length > 0 && (
          <span className="rounded-full bg-tcs-inverse px-1.5 text-[10px] text-tcs-inverse-ink">
            {selected.length}
          </span>
        )}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" {...overlayClickClose(() => setOpen(false))} />
          <div className="absolute left-0 top-8 z-30 max-h-72 w-64 overflow-hidden rounded-md border border-tcs-border bg-tcs-overlay shadow-lg">
            <div className="border-b border-tcs-border p-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search hospitals…"
                className="w-full rounded border border-tcs-border px-2 py-1 text-xs focus:border-tcs-border-input focus:outline-none"
              />
            </div>
            <ul className="max-h-52 overflow-y-auto py-1 text-xs">
              {filtered.slice(0, 200).map((o) => (
                <li key={o.account_id}>
                  <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-tcs-bg-muted">
                    <input
                      type="checkbox"
                      checked={selected.includes(o.account_id)}
                      onChange={() => onToggle(o.account_id)}
                    />
                    <span className="truncate">{o.account_name ?? o.account_id}</span>
                  </label>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-center text-tcs-text-muted">No matches.</li>
              )}
            </ul>
            {selected.length > 0 && (
              <div className="border-t border-tcs-border p-2 text-right">
                <button
                  type="button"
                  onClick={onClear}
                  className="text-[11px] text-tcs-text-muted hover:text-tcs-text"
                >
                  Clear ({selected.length})
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 text-xs transition ${
        active
          ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
          : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-bg-muted'
      }`}
    >
      {label}
    </button>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  ariaLabel: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex items-center gap-0.5 rounded-md border border-tcs-border bg-tcs-raised p-0.5 text-xs"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded px-2.5 py-1 ${
            value === o.value
              ? 'bg-tcs-surface font-medium text-tcs-text'
              : 'text-tcs-text-secondary hover:text-tcs-text'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
